import { Check, Download, Share, Smartphone } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/features/common/ui';
import { INSTALL_INSTRUCTIONS } from './install';
import { useInstallPrompt } from './useInstallPrompt';

/**
 * "Put Fint on your home screen", on Settings.
 *
 * Four states, one per thing the browser lets us do. Where it offers a
 * prompt (Chrome and Edge, on Android and on a laptop) there is a button,
 * because a button beats a paragraph. On iPhone and iPad no browser offers
 * one, so the two taps are spelled out with the Share glyph the person is
 * looking for - it is the one control on that screen with no label. Once
 * installed, the card says so rather than vanishing: somebody who cannot
 * find the icon should be told it exists. And a browser that offers nothing
 * gets the generic sentence, which is true everywhere.
 */
export function InstallCard() {
  const { canPrompt, promptInstall, installed, standalone, platform } = useInstallPrompt();
  const instructions = INSTALL_INSTRUCTIONS[platform];
  const isApple = platform === 'iphone' || platform === 'ipad';

  return (
    <Card data-testid="install-card" data-platform={platform}>
      <CardHeader>
        <CardTitle>
          <Smartphone className="size-4 text-muted-foreground" aria-hidden />
          Put Fint on your home screen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {installed ? (
          <p className="flex items-center gap-2" data-testid="install-installed">
            <Check className="size-4 text-success" aria-hidden />
            {standalone
              ? 'Installed ✓ — you are using it now.'
              : 'Installed ✓ — open it from your home screen.'}
          </p>
        ) : canPrompt ? (
          <>
            <p className="text-muted-foreground">
              Opens in its own window, with an icon beside your other apps, and still opens when
              the connection is poor.
            </p>
            <Button
              onClick={() => {
                // A used prompt rejects; the store drops it first, so this
                // cannot be reached twice, but a rejection here is not news.
                void promptInstall().catch(() => undefined);
              }}
              data-testid="install-prompt"
            >
              <Download aria-hidden />
              Install Fint
            </Button>
          </>
        ) : isApple ? (
          <>
            <p className="text-muted-foreground">{instructions.title}. Two taps in Safari:</p>
            <ol className="space-y-1.5" data-testid="install-steps">
              {instructions.steps.map((step, index) => (
                <li key={step} className="flex items-center gap-2">
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                    {index + 1}
                  </span>
                  {/* The Share glyph beside the word, because the word is
                      not on the button - only the glyph is. */}
                  {index === 0 ? <Share className="size-4 shrink-0 text-primary" aria-hidden /> : null}
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </>
        ) : (
          <>
            <p className="text-muted-foreground">{instructions.title}.</p>
            {/* Android's two menu taps are a list; the desktop's one sentence
                is a sentence. Numbering a single step is a list of one. */}
            {instructions.steps.length > 1 ? (
              <ol className="space-y-1.5" data-testid="install-steps">
                {instructions.steps.map((step, index) => (
                  <li key={step} className="flex items-center gap-2">
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p data-testid="install-steps">{instructions.steps[0]}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
